import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { Video as VideoIcon } from "lucide-react";
import { Note } from "protogen/notes_pb";
import { Suggestions } from "protogen/suggestions_pb";
import { VideoRoom, VideoRoom_State } from "protogen/video_service_pb";
import { useRunWorkflow } from "services/extraction";
import { SuggestionsList } from "components/activity/Suggestions";
import SuggestionActions, {
  SuggestionActionsHandle,
} from "components/activity/SuggestionActions";
import { RunWorkflowRequest } from "protogen/extraction_service_pb";
import { SuggestionEntityType } from "protogen/suggestions_pb";
import Spinner from "components/common/Spinner";
import NotesEditor from "./NotesEditor";
import VideoCallModule from "components/video/VideoCallModule";
import VideoCallSetup from "components/video/VideoCallSetup";
import { LocalUserChoices } from "@livekit/components-react";

type SuggestionsSectionProps = {
  videoRoom: VideoRoom;
  suggestions: Suggestions;
  familyRef: string;
  title: string;
};

const Loader = () => (
  <Box
    display="flex"
    flexDirection="row"
    gap="10px"
    alignItems="center"
    sx={{
      width: "100%",
      border: "1px solid #D4D4D4",
      borderRadius: "12px",
      padding: "16px 12px",
      boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
    }}
  >
    <Spinner size={20} />
    <Typography color="#198282" variant="body" fontWeight={600}>
      Analyzing call
    </Typography>
  </Box>
);

const SuggestionsSection = ({
  videoRoom,
  suggestions,
  familyRef,
}: SuggestionsSectionProps) => {
  const actionsRef = React.useRef<SuggestionActionsHandle | null>(null);
  const { request: workflowRequest, loading } = useRunWorkflow();
  const isFreshlyCompleteWithoutSuggestions =
    videoRoom.state === VideoRoom_State.COMPLETED &&
    // Updated within the last 30 seconds
    Date.now() - Number(videoRoom.lastUpdatedSec) * 1000 < 30000 &&
    !suggestions.hasSuggestions;
  const handleClick = async () => {
    await workflowRequest(
      new RunWorkflowRequest({
        workflowName: "process-video-call",
        arguments: [],
        entityType: SuggestionEntityType.VIDEOCALL,
        entityRef: videoRoom.uuid,
      }),
    );
  };
  return (
    <>
      {videoRoom.state === VideoRoom_State.ACTIVE && (
        <Typography variant="bodySmall">
          Summary and task suggestions will be generated at the end of the call.{" "}
          <span
            style={{
              color: "#198282",
              fontWeight: "600",
              cursor: "pointer",
            }}
            onClick={handleClick}
          >
            Try now?
          </span>
        </Typography>
      )}
      <Box
        display="flex"
        flexDirection="column"
        gap="24px"
        mb="16px"
        width="100%"
      >
        {(loading || isFreshlyCompleteWithoutSuggestions) && <Loader />}
        {!loading && suggestions?.hasSuggestions && (
          <SuggestionsList
            inlineMode={true}
            suggestions={suggestions}
            actions={{
              suggestTask: (t) => actionsRef.current?.suggestTask(t),
              suggestFact: (f) => actionsRef.current?.suggestFact(f),
              suggestEvent: (e) => actionsRef.current?.suggestEvent(e),
            }}
            isOpen={true}
            entityRef={videoRoom.ref}
            entityType={"videocall"}
            familyRef={familyRef}
          />
        )}
      </Box>
      <SuggestionActions ref={actionsRef} familyRef={familyRef} />
    </>
  );
};

type Props = {
  handleCallEnd: () => void;
  note: Note | null;
  token: string;
  isCallActive: boolean;
  onReJoin: () => void;
  videoRoom: VideoRoom | null;
  suggestions: Suggestions | null;
  familyRef: string;
  onVideoSetupJoin?: (choices: LocalUserChoices) => void;
  wasCreatingState?: boolean;
  userChoices?: LocalUserChoices | null;
};

export default ({
  handleCallEnd,
  note,
  token,
  isCallActive,
  onReJoin,
  videoRoom,
  suggestions,
  familyRef,
  onVideoSetupJoin,
  userChoices,
}: Props) => {
  if (videoRoom?.status === "creating") {
    return (
      <VideoCallSetup
        videoRoomUuid={videoRoom.uuid}
        familyRef={familyRef}
        onJoin={onVideoSetupJoin || (() => {})}
      />
    );
  }

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap={"32px"}
      marginTop={"32px"}
      paddingRight="20px"
      paddingLeft="20px"
    >
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={"12px"}
        marginBottom={"32px"}
      >
        {isCallActive ? (
          <VideoCallModule
            onCallEnd={handleCallEnd}
            containerized={true}
            token={token}
            initialUserChoices={userChoices}
            skipPreJoin={!!userChoices}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "400px",
              gap: 3,
              backgroundColor: "#f5f5f5",
              borderRadius: "8px",
              border: "1px dashed #ccc",
            }}
          >
            <Typography variant="h5" gutterBottom>
              Video Call Ended
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, textAlign: "center" }}>
              The video call has ended. Click below to rejoin.
            </Typography>
            <Button
              variant="contained"
              startIcon={<VideoIcon />}
              onClick={onReJoin}
            >
              Re-join call
            </Button>
          </Box>
        )}

        {videoRoom && suggestions && (
          <SuggestionsSection
            videoRoom={videoRoom}
            suggestions={suggestions}
            familyRef={familyRef}
            title={"Smart Suggestions"}
          />
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4">Notes</Typography>
        </Box>

        {note && (
          <NotesEditor
            note={note}
            title={"Notes"}
            initialContent={""}
            style={{ border: "none", padding: "0", boxShadow: "none" }}
            editorType="simple"
          />
        )}
      </Box>
    </Box>
  );
};
